'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import MobileSidebar from '../header/mobile-sidebar';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { GoArrowUpRight } from 'react-icons/go';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCurrentStore } from '../../utils/use-store-utils';
import { usePathname } from 'next/navigation';
import { publishedRsmotoMenuFields } from '../../utils/unpublished-rsmoto-content';

export function Navbar({ generalLayout, menu }: { generalLayout: GeneralLayout; menu: Menu[] }) {
  menu = menu.map(item => ({ ...item, fields: publishedRsmotoMenuFields(item.fields ?? []) }));
  const { isGasGas, isCFMoto, isKtmBratislava } = useCurrentStore();

  const pathname = usePathname();

  const isContactPage = pathname === '/kontakt';

  const gasgasLogo = '/images/logo/gasgas-contact.svg';
  const cfmotoLogo = '/images/logo/cfmoto-contact.svg';
  const ktmBratislavaLogo = '/images/logo/ktm-contact.svg';

  return (
    <header
      className={cn(
        'custom-section',
        'border-b-2 border-b-primary bg-black py-4 sm:py-5 md:py-6 lg:py-[30px]',
        pathname === '/dakujeme-za-vas-zaujem' && 'bg-black/50 backdrop-blur-lg',
        pathname === '/kontakt' && 'bg-white',
      )}
    >
      <div className={cn('custom-container', 'flex items-center justify-between')}>
        <Link
          prefetch={false}
          href={ROUTES.HOME()}
          aria-label={`${isGasGas ? 'GASGAS Bratislava' : isCFMoto ? 'CFMOTO Bratislava' : 'KTM Bratislava'} – domov`}
        >
          <Image
            src={
              isContactPage
                ? isGasGas
                  ? gasgasLogo
                  : isCFMoto
                    ? cfmotoLogo
                    : ktmBratislavaLogo
                : (generalLayout?.data.header?.logo?.url ?? '')
            }
            alt={resolveImageAlt(
              isContactPage ? undefined : generalLayout?.data?.header?.logo,
              'Logo',
            )}
            width={0}
            height={0}
            className={cn('w-auto object-contain object-center', {
              'h-14 sm:h-16 lg:h-20': isGasGas,
              'h-7 sm:h-10': isCFMoto || isKtmBratislava,
            })}
            sizes="300px"
          />
        </Link>

        <div className="flex items-center justify-end gap-4 lg:gap-5 xl:gap-6">
          <div className="hidden items-center justify-end gap-4 lg:gap-5 xl:flex xl:gap-6">
            {menu[0]?.fields?.map(item => (
              <Link
                key={item.name}
                href={getSafeHref(item?.path ?? '')}
                className={cn(
                  'text-sm font-medium tracking-tight text-white lg:text-base',
                  isContactPage && 'text-black',
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>
          <Button
            asChild
            className={cn('hidden md:flex', isGasGas && '!text-white', isCFMoto && '!text-white')}
          >
            <Link prefetch={false} href={'https://rsmoto.sk'} target="_blank">
              Navštíviť e-shop <GoArrowUpRight size={18} />
            </Link>
          </Button>
          <div className="flex items-center justify-center xl:hidden">
            <MobileSidebar
              menu={menu[0] && menu[0].fields}
              side="right"
              className={{ trigger: cn(isContactPage && 'text-black') }}
            />
          </div>
        </div>
      </div>
    </header>
  );
}
